import styled from '@emotion/styled';
import debounce from '@mui/material/utils/debounce';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';

import { Size } from '../../styling/AppStyles';
import {
  GeocodeServiceResponse,
  GeocodeServiceResponseCode,
  getAutocompleteService,
  getGeocodeService,
  PlacesServiceResponse,
  PlacesServiceResponseCode,
} from '../../vendors/GoogleMaps';
import ExinAsyncSelectInner from '../styled/ExinAsyncSelect';
import ExinMuiSlider from '../styled/ExinMuiSlider';
import ExinText from '../styled/ExinText';

/**
 * A drop-down option for a named location from Google. For example, "Amsterdam, Netherlands".
 */
type LocationOption = {
  label: string;
  value: string;
};

/**
 * The information shared with the rest of the app about a particular location chosen by the user.
 *
 * Besides containing a human-readable label (see {@link LocationOption}), latitude/longitude and
 * a range (km) chosen from a slider input.
 */
export type Location = LocationOption & {
  lat: number;
  lng: number;
  range: number;
};

/**
 * A debounced function to query google's autocomplete service for finding places matching a
 * search term.
 */
const loadLocationOptions = debounce(
  (searchTerm: string, complete: (places: Array<LocationOption>) => void) => {
    getAutocompleteService().getPlacePredictions(
      { input: searchTerm },
      (predictions: PlacesServiceResponse, serviceStatus: PlacesServiceResponseCode) => {
        if (serviceStatus !== PlacesServiceResponseCode.OK) {
          complete([]);
        }
        complete(
          predictions.map((prediction) => {
            return {
              value: prediction.place_id,
              label: prediction.description,
            };
          })
        );
      }
    );
  },
  500
);

export const StepLocationPlaceSearch: FunctionComponent<{
  onChange: (place: null | LocationOption) => void;
}> = ({ onChange }) => {
  return (
    <ExinAsyncSelectInner
      cacheOptions
      loadOptions={loadLocationOptions}
      // @ts-ignore
      // Something in react-select#Async demands onChange data to be `unknown`
      // TypeScript will complain, because our own data is in fact _not_ unknown.
      onChange={onChange}
      placeholder="Fill in your city"
    />
  );
};

const StepLocationRangeContainer = styled.div`
  margin-top: ${Size.medium};
`;

const StepLocationRangeLabelWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StepLocation: FunctionComponent<{
  rangeMin: number;
  rangeMax: number;
  rangeStep: number;
  onChange: (location: Location) => void;
}> = ({ rangeMin, rangeMax, rangeStep, onChange }) => {
  const [placeInfo, setPlaceInfo] = useState<null | Omit<Location, 'range'>>(null);
  const handlePlaceChange = useCallback((place: LocationOption | null) => {
    if (!place) {
      return;
    }
    getGeocodeService().geocode(
      { placeId: place.value },
      (results: GeocodeServiceResponse, status: GeocodeServiceResponseCode) => {
        if (status !== GeocodeServiceResponseCode.OK) {
          throw new Error(
            `Geocoding query for a place with an ID of '${place.value}' failed - response status: ${status}`
          );
        }
        setPlaceInfo({
          ...place,
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        });
      }
    );
  }, []);

  const [range, setRange] = useState<Location['range']>(100);
  const handleRangeChange = useCallback(
    (event: Event) => setRange((event.target as any)?.value as number),
    [setRange]
  );

  useEffect(() => {
    if (!placeInfo) {
      return;
    }
    onChange({ ...placeInfo, range });
  }, [placeInfo, range, onChange]);

  return (
    <>
      <StepLocationPlaceSearch onChange={handlePlaceChange} />

      <StepLocationRangeContainer>
        <ExinText>
          Set maximum travel distance ({range}
          {range >= 250 ? '+' : ''} km)
        </ExinText>

        <ExinMuiSlider
          value={range}
          min={rangeMin}
          max={rangeMax}
          step={rangeStep}
          onChange={handleRangeChange}
        />

        <StepLocationRangeLabelWrapper>
          <ExinText>{rangeMin} km</ExinText>
          <ExinText>
            {rangeMax}
            {range === 250 ? '+' : ''} km
          </ExinText>
        </StepLocationRangeLabelWrapper>
      </StepLocationRangeContainer>
    </>
  );
};

export default StepLocation;
